import React from 'react'
import styled from 'styled-components'
import { Draggable } from 'react-beautiful-dnd'
import i18n from 'live/services/i18n'
import { ColorStyle, CustomSettings, DEFAULT_THRESHOLD_COLOR, HIDE_COLOR_VALUE } from '../api'
import { ChannelRowData, ColorMode, getColorMode, getDotColor, getSeedCustomColor, getTextTintColor } from './channelRowHelpers'
import { ColorDot, RowColorMark, RowInfo, RowMeta, RowMetaLine, RowTitle } from './rowStyles'
import { DynamicChannelBadge } from './DynamicChannelBadge'

type Props = {
    row: ChannelRowData
    index: number
    expanded: boolean
    colorStyle: ColorStyle
    colorTextEnabled: boolean
    onRemove: (mnemonic: string) => void
    onToggleExpanded: (mnemonic: string) => void
    onUpdateSettings: (mnemonic: string, patch: Partial<CustomSettings>) => void
}

// parses a threshold number input's raw string value: empty/blank clears the override (falls back to
// the channel metadata's own threshold, or no threshold at all if metadata doesn't set one either)
const parseThresholdInput = (rawValue: string): number | null => {
    if (rawValue.trim() === '') return null

    const parsed = Number(rawValue)

    return Number.isFinite(parsed) ? parsed : null
}

export const SelectedChannelRow = React.memo(function SelectedChannelRow({
    row,
    index,
    expanded,
    colorStyle,
    colorTextEnabled,
    onRemove,
    onToggleExpanded,
    onUpdateSettings
}: Props): JSX.Element {
    const { metadata, settings } = row
    const effectiveTitle = settings?.customTitle ?? metadata.title
    const isCustomTitle = !!settings?.customTitle
    const colorMode = getColorMode(settings)
    const info = metadata.category ? [metadata.mnemonic, `Category: ${metadata.category}`] : [metadata.mnemonic]
    const tintColor = colorTextEnabled ? getTextTintColor(row) : null

    const effectiveMinThreshold = settings?.customMinThresholds ?? metadata.minThresholds
    const effectiveMaxThreshold = settings?.customMaxThresholds ?? metadata.maxThresholds
    const effectiveMinThresholdColor = settings?.customMinThresholdColor ?? metadata.minThresholdColor ?? DEFAULT_THRESHOLD_COLOR
    const effectiveMaxThresholdColor = settings?.customMaxThresholdColor ?? metadata.maxThresholdColor ?? DEFAULT_THRESHOLD_COLOR

    const handleRename = (value: string): void => {
        const sanitizedValue = value.trim() === '' ? null : value

        onUpdateSettings(metadata.mnemonic, { customTitle: sanitizedValue === metadata.title ? null : sanitizedValue })
    }

    const handleResetName = (): void => {
        onUpdateSettings(metadata.mnemonic, { customTitle: null })
    }

    const handleColorMode = (mode: ColorMode, customColor?: string): void => {
        const customColorValue = mode === 'default' ? null : mode === 'hidden' ? HIDE_COLOR_VALUE : customColor

        onUpdateSettings(metadata.mnemonic, { customColor: customColorValue })
    }

    const handleMinThresholdChange = (value: string): void => {
        onUpdateSettings(metadata.mnemonic, { customMinThresholds: parseThresholdInput(value) })
    }

    const handleMaxThresholdChange = (value: string): void => {
        onUpdateSettings(metadata.mnemonic, { customMaxThresholds: parseThresholdInput(value) })
    }

    const handleMinThresholdColorChange = (color: string): void => {
        onUpdateSettings(metadata.mnemonic, { customMinThresholdColor: color })
    }

    const handleMaxThresholdColorChange = (color: string): void => {
        onUpdateSettings(metadata.mnemonic, { customMaxThresholdColor: color })
    }

    return (
        <Draggable draggableId={metadata.mnemonic} index={index}>
            {provided => (
                <RowWrapper ref={provided.innerRef} {...provided.draggableProps}>
                    <Row>
                        <RowMain>
                            <DragHandle className="fas fa-grip-lines" {...provided.dragHandleProps} />
                            <RowColorMark color={getDotColor(row)} shape={colorStyle} />
                            <RowInfo>
                                <RowTitle style={tintColor ? { color: tintColor } : undefined}>{effectiveTitle}</RowTitle>
                                <RowMetaLine>
                                    {metadata.selected === true && <DynamicChannelBadge />}
                                    <RowMeta>{info.join(' · ')}</RowMeta>
                                </RowMetaLine>
                            </RowInfo>
                        </RowMain>
                        <RowActions>
                            <CustomizeButton onClick={() => onToggleExpanded(metadata.mnemonic)}>
                                <i className="fa fa-cog" />
                                {i18n('Customize')}
                            </CustomizeButton>
                            <RemoveButton onClick={() => onRemove(metadata.mnemonic)}>
                                <i className="fa fa-times" />
                                {i18n('Remove')}
                            </RemoveButton>
                        </RowActions>
                    </Row>
                    {expanded && (
                        <Drawer>
                            <DrawerRow>
                                <DrawerLabel>{i18n('Name')}</DrawerLabel>
                                <input
                                    type="text"
                                    value={effectiveTitle}
                                    onChange={event => handleRename(event.target.value)}
                                />
                                {isCustomTitle && (
                                    <ResetButton onClick={handleResetName}>
                                        <i className="fa fa-undo" />
                                        {i18n('Reset')}
                                    </ResetButton>
                                )}
                            </DrawerRow>
                            <DrawerRow>
                                <DrawerLabel>{i18n('Color')}</DrawerLabel>
                                <ColorModes>
                                    <ModeButton
                                        className={colorMode === 'default' ? 'active' : ''}
                                        onClick={() => handleColorMode('default')}
                                    >
                                        <ColorDot color={metadata.color ?? 'var(--gray-400)'} />
                                        {i18n('Default')}
                                    </ModeButton>
                                    <ModeButtonLabel className={colorMode === 'custom' ? 'active' : ''}>
                                        <input
                                            type="color"
                                            value={getSeedCustomColor(row)}
                                            onChange={event => handleColorMode('custom', event.target.value)}
                                        />
                                        {i18n('Custom')}
                                    </ModeButtonLabel>
                                    <ModeButton
                                        className={colorMode === 'hidden' ? 'active' : ''}
                                        onClick={() => handleColorMode('hidden')}
                                    >
                                        <i className="fa fa-eye-slash" />
                                        {i18n('Hide')}
                                    </ModeButton>
                                </ColorModes>
                            </DrawerRow>
                            <DrawerRow>
                                <DrawerLabel>{i18n('Min')}</DrawerLabel>
                                <input
                                    type="number"
                                    placeholder={i18n('No minimum')}
                                    value={effectiveMinThreshold ?? ''}
                                    onChange={event => handleMinThresholdChange(event.target.value)}
                                />
                                <ThresholdSwatch>
                                    <input
                                        type="color"
                                        value={effectiveMinThresholdColor}
                                        onChange={event => handleMinThresholdColorChange(event.target.value)}
                                    />
                                </ThresholdSwatch>
                            </DrawerRow>
                            <DrawerRow>
                                <DrawerLabel>{i18n('Max')}</DrawerLabel>
                                <input
                                    type="number"
                                    placeholder={i18n('No maximum')}
                                    value={effectiveMaxThreshold ?? ''}
                                    onChange={event => handleMaxThresholdChange(event.target.value)}
                                />
                                <ThresholdSwatch>
                                    <input
                                        type="color"
                                        value={effectiveMaxThresholdColor}
                                        onChange={event => handleMaxThresholdColorChange(event.target.value)}
                                    />
                                </ThresholdSwatch>
                            </DrawerRow>
                        </Drawer>
                    )}
                </RowWrapper>
            )}
        </Draggable>
    )
})

const RowWrapper = styled.div`
    &:not(:last-child) {
        border-bottom: 0.5px solid var(--gray-300);
    }
`

// wraps when RowMain and RowActions no longer both fit on one line - RowMain's own min-width is
// the threshold: once the row is narrower than that plus RowActions' width, RowActions moves to
// its own line below instead of squeezing both groups together
const Row = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    min-height: 48px;
    padding: 8px 16px 8px 12px;
`

const RowMain = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 140px;
    min-width: 140px;
`

const RowActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    margin-left: auto; // hugs the right edge, whether on the same line as RowMain or wrapped below
`

const DragHandle = styled.i`
    flex-shrink: 0;
    width: 14px;
    text-align: center;
    color: var(--blue-500);
    cursor: grab;
`

const CustomizeButton = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 18px;
    color: var(--blue-500);
    background: none;
    border: none;
    padding: 4px 6px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        background-color: var(--gray-200);
    }
`

const RemoveButton = styled(CustomizeButton)`
    color: var(--red-500);
`

const Drawer = styled.div`
    padding: 10px 16px 14px 42px;
    background-color: var(--gray-100);
`

const DrawerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;

    &:last-child {
        margin-bottom: 0;
    }

    > input[type='text'],
    > input[type='number'] {
        flex: 1;
        height: 30px;
        padding: 0 8px;
        border: 1px solid var(--gray-400);
        border-radius: 4px;
        background-color: var(--gray-0);
        color: var(--gray-1000);
        font-size: 13px;
    }
`

const DrawerLabel = styled.span`
    flex-shrink: 0;
    width: 44px;
    font-size: 12px;
    color: var(--gray-800);
`

const ResetButton = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--blue-500);
    background: none;
    border: none;
    padding: 4px 6px;
    cursor: pointer;
`

const ColorModes = styled.div`
    display: flex;
    gap: 6px;
`

const modeButtonStyles = `
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    padding: 6px 10px;
    border: 1px solid var(--gray-400);
    border-radius: 4px;
    background: var(--gray-0);
    color: var(--gray-800);
    cursor: pointer;

    &.active {
        border-color: var(--blue-500);
        color: var(--blue-500);
    }
`

const ModeButton = styled.button`
    ${modeButtonStyles}
`

const ModeButtonLabel = styled.label`
    ${modeButtonStyles}

    > input[type='color'] {
        width: 14px;
        height: 14px;
        padding: 0;
        border: none;
        border-radius: 50%;
        cursor: pointer;
    }
`

// a plain swatch (no Default/Hide modes like the channel's own color) - a threshold either has a
// color or falls back to the shared default red, there's no "hide the threshold color" concept
const ThresholdSwatch = styled.label`
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border: 1px solid var(--gray-400);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    > input[type='color'] {
        width: 100%;
        height: 100%;
        padding: 0;
        border: none;
        border-radius: 4px;
        cursor: pointer;
    }
`
